import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  Auth,
  AuthCredential,
  GoogleAuthProvider,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  User,
  browserLocalPersistence,
  getMultiFactorResolver,
  multiFactor,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { NotificationService } from '@services/notification.service';
import { UserService } from 'app/services/user.service';
import { MatDialog } from '@angular/material/dialog';
import { RequestPhonePopupComponent } from '@shared/components/request-phone-popup/request-phone-popup.component';
import { RequestVerificationCodePopupComponent } from '@shared/components/request-verification-code-popup/request-verification-code-popup.component';
import { AuthSetupSuccessPopupComponent } from '@shared/components/auth-setup-success-popup/auth-setup-success-popup.component';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _afAuth: Auth = inject(Auth);
  private _userService: UserService = inject(UserService);
  private _notifications = inject(NotificationService);
  private _router = inject(Router);
  private _dialog = inject(MatDialog);
  public _translate: TranslateService = inject(TranslateService);

  authStatusSub: Observable<User>;
  appVerifier: RecaptchaVerifier;

  constructor() {
    this.authStateListener();
  }

  authStateListener() {
    this.authStatusSub = new Observable<User>(observer => this._afAuth.onAuthStateChanged(user => observer.next(user)));
  }

  login(value) {
    this._afAuth.setPersistence(browserLocalPersistence);

    signInWithEmailAndPassword(this._afAuth, value.email, value.password)
      .then(() => {
        this._router.navigate(['./dashboard']);
      })
      .catch(() => {
        const message = this._translate.instant('keywords.invalid_username_or_passowrd');
        this._notifications.showMessage(message, 1000);
      });
  }

  async sendPasswordResetEmail(email: string) {
    try {
      await sendPasswordResetEmail(this._afAuth, email);
      const message = 'Password reset email sent. Please check your inbox.';
      this._notifications.showMessage(message, 4000);
    } catch (error: any) {
      console.error('Password reset failed:', error);
      const message = error.code === 'auth/user-not-found'
        ? 'No account found with that email.'
        : 'Unable to send password reset email. Please try again.';
      this._notifications.showMessage(message, 4000);
    }
  }

  async login2FA(value) {
    try {
      await signInWithEmailAndPassword(this._afAuth, value.email, value.password);

      this._router.navigate(['./dashboard']);
    } catch (error: any) {
      if (error.code === 'auth/multi-factor-auth-required') {
        try {
          await this.resolveMfaChallenge(error);
          this._router.navigate(['./dashboard']);
        } catch (mfaError) {
          const message = this._translate.instant('keywords.2FA_sign_in_error');
          this._notifications.showMessage(message, 1000);
          console.error('2FA Sign-In Error:', mfaError);
        }
      } else {
        const message = this._translate.instant('keywords.invalid_username_or_passowrd');
        this._notifications.showMessage(message, 1000);
        console.error('Login failed:', error);
      }
    }
  }

  // Re-authenticates the current user, transparently completing the 2FA challenge
  // (SMS code) if the account has multi-factor auth enrolled.
  async reauthenticateWithMfa(user: User, credential: AuthCredential): Promise<void> {
    try {
      await reauthenticateWithCredential(user, credential);
    } catch (error: any) {
      if (error.code === 'auth/multi-factor-auth-required') {
        await this.resolveMfaChallenge(error);
      } else {
        throw error;
      }
    }
  }

  private async resolveMfaChallenge(error: any): Promise<void> {
    this.generateReCaptcha();
    const resolver = getMultiFactorResolver(this._afAuth, error);

    const selectedHint = resolver.hints[0];
    const phoneInfoOptions = {
      multiFactorHint: selectedHint,
      session: resolver.session
    };

    const phoneAuthProvider = new PhoneAuthProvider(this._afAuth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, this.appVerifier);

    const verificationCode = await this.requestCode();
    const phoneCredential = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneCredential);

    await resolver.resolveSignIn(multiFactorAssertion);
  }

  generateReCaptcha() {
    if (!this.appVerifier) {
      this.appVerifier = new RecaptchaVerifier('recaptcha-container', { size: 'invisible' }, this._afAuth);
    }
  }

  requestPhone(cancelAction: () => void = null): Promise<string | null> {
    return new Promise(resolve => {
      this._dialog.open(RequestPhonePopupComponent, {
        maxWidth: '480px',
        disableClose: true,
        data: {
          confirmAction: (result: string | null) => {
            resolve(result);
          },
          cancelAction: cancelAction
        }
      });
    });
  }

  requestCode(cancelAction: () => void = null): Promise<string | null> {
    return new Promise(resolve => {
      this._dialog.open(RequestVerificationCodePopupComponent, {
        maxWidth: '480px',
        disableClose: true,
        data: {
          confirmAction: (result: string | null) => {
            resolve(result);
          },
          cancelAction: cancelAction,
        }
      });
    });
  }

  // Use +1 345-111-1111 with 444444
  async enrollMfa(cancelAction: () => void = null): Promise<boolean> {
    const user = this._afAuth?.currentUser;
    if (!user) {
      return;
    }

    try {
      this.generateReCaptcha();
      const phoneNumber = await this.requestPhone(cancelAction);
      if (!phoneNumber) {
        return false;
      }

      const phoneAuthProvider = new PhoneAuthProvider(this._afAuth);
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneNumber, this.appVerifier);

      const verificationCode = await this.requestCode(cancelAction);
      if (!verificationCode) {
        return false;
      }
      const phoneCredential = PhoneAuthProvider.credential(verificationId, verificationCode);

      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(phoneCredential);
      //Used by Firebase to get user data during the 2FA enrollment:
      await multiFactor(user).enroll(multiFactorAssertion, 'Phone Number');
      this._dialog.open(AuthSetupSuccessPopupComponent);
      return true;
    } catch (mfaError) {
      const message = this._translate.instant('keywords.error_enrolling_2FA');
      this._notifications.showMessage(message, 1000);
      console.error('Error Enrolling 2FA:', mfaError);
      return false;
    }
  }

  async checkIfUserHasEnrolled2FA(): Promise<boolean> {
    const user = this._afAuth.currentUser;
    if (!user) {
      return;
    }
    try {
      const enrolledFactors = multiFactor(user).enrolledFactors;

      if (enrolledFactors.length > 0) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      const message = this._translate.instant('keywords.error_checking_2FA_enrollment');
      this._notifications.showMessage(message, 1000);
      console.error('Error checking 2FA enrollment:', error);
      return false;
    }
  }

  onGoogleLoginClick() {
    const provider = new GoogleAuthProvider();
    signInWithPopup(this._afAuth, provider)
      .then(() => {
        // This gives you a Google Access Token. You can use it to access the Google API.
        // more documentation available at:
        // https://firebase.google.com/docs/auth/web/google-signin

        this._router.navigate(['./dashboard']);
      })
      .catch(error => {
        console.log(error);
      });
  }

  logout() {
    this._afAuth.signOut().then(() => {
      this._userService.clearCachedData('user_logedin');
      this._userService.userSubject.next(null);
      this._router.navigate(['/profile/login']);
    });
  }
}
